import React from 'react';
import Dialog from './modals/Dialog';
import { t } from '@lingui/macro';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

export default function NDISelector({ open, onClose, onProceed, restreamer, i18n, notify }) {
	const [state, setState] = React.useState({ loading: false, devices: [], mdns: [], avahi: [], selected: null, preview: null, previewLoading: false });

	React.useEffect(() => {
		if (open) refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const extractNameFromLine = (l) => {
		if (!l || typeof l !== 'string') return null;

		// Try single-quoted name: 'NAME'
		let m = l.match(/'([^']+)'/);
		if (m && m[1]) return m[1];

		// Try double-quoted name: "NAME"
		m = l.match(/\"([^\"]+)\"/);
		if (m && m[1]) return m[1];

		// Try pattern: ] NAME Found or similar — extract last token-like group
		m = l.match(/Found\s+[0-9]+\s+NDI\s+sources:\s*(.*)$/i);
		if (m && m[1]) return m[1].trim();

		// As fallback, try to find a token looking like a hostname with parentheses
		m = l.match(/([A-Za-z0-9_-]+\s*\(.+\))/);
		if (m && m[1]) return m[1].trim();

		// Last resort: return the full line truncated
		return l.length > 0 ? l : null;
	};

	const normalize = (res) => {
		if (!res || res.err !== null) return [];
		const v = res.val;
		if (!v) return [];

		let arr = [];
		if (Array.isArray(v)) {
			arr = v;
		} else if (v.devices && Array.isArray(v.devices)) {
			arr = v.devices;
		} else if (v.services && Array.isArray(v.services)) {
			arr = v.services;
		} else {
			arr = [];
		}

		// Map each entry to a human-readable name string when possible
		const names = arr.map((d) => {
			if (!d) return null;
			if (typeof d === 'string') return d;
			if (typeof d === 'object') {
				if (d.name) return d.name;
				if (d.instance) return d.instance;
				if (d.service) return d.service;
				if (d.raw) {
					const n = extractNameFromLine(d.raw);
					if (n) return n;
				}
				if (d.line) {
					const n = extractNameFromLine(d.line);
					if (n) return n;
				}
			}
			return null;
		}).filter((x) => x !== null);

		return names;
	};

	const refresh = async () => {
		setState((s) => ({ ...s, loading: true }));
		try {
			const d = await restreamer.NDIDevices();
			const m = await restreamer.NDIMDNSDevices();
			const a = await restreamer.NDIAvahiDevices();

			setState((s) => ({
				...s,
				loading: false,
				devices: normalize(d),
				mdns: normalize(m),
				avahi: normalize(a),
			}));
		} catch (e) {
			setState((s) => ({ ...s, loading: false }));
			if (notify) notify.Dispatch('error', 'ndi:discover', i18n ? i18n._(t`Failed to discover NDI devices`) : 'Failed to discover NDI devices');
		}
	};

	const doPreview = async (source) => {
		setState((s) => ({ ...s, previewLoading: true, preview: null }));
		try {
			const res = await restreamer.NDIPreview({ name: source });
			if (res && res.err === null && res.val && res.val.image) {
				setState((s) => ({ ...s, previewLoading: false, preview: res.val.image }));
				} else {
					setState((s) => ({ ...s, previewLoading: false }));
					// Prefer structured ffmpeg output if provided by the API for diagnostics
					let msg = 'Preview failed';
					if (res) {
						if (res.err && res.err.message) {
							msg = res.err.message;
						}
						// Sometimes the server returns a JSON body with ffmpeg output inside the error message/object
						if (typeof msg === 'object' && msg.ffmpeg) {
							msg = msg.ffmpeg;
						}
						if (res.val && res.val.ffmpeg) {
							msg = res.val.ffmpeg;
						}
					}
					if (notify) notify.Dispatch('error', 'ndi:preview', i18n ? i18n._(t`Preview failed: ${msg}`) : `Preview failed: ${msg}`);
					console.error('NDI preview failed:', msg);
					// Log full response for debugging (includes ffmpeg output or server error)
					console.error('NDI preview response:', res);
				}
		} catch (e) {
			setState((s) => ({ ...s, previewLoading: false }));
			if (notify) notify.Dispatch('error', 'ndi:preview', i18n ? i18n._(t`Preview failed`) : 'Preview failed');
		}
	};

	const handleProceed = () => {
		if (!state.selected) return;
		onProceed(state.selected);
		onClose();
	};

	return (
		<Dialog
			open={open}
			title={i18n ? i18n._(t`NDI Sources`) : 'NDI Sources'}
			onClose={onClose}
			buttonsLeft={<Button variant="outlined" color="default" onClick={onClose}>{i18n ? i18n._(t`Cancel`) : 'Cancel'}</Button>}
			buttonsRight={<Button variant="outlined" color="primary" onClick={handleProceed} disabled={!state.selected}>{i18n ? i18n._(t`Proceed`) : 'Proceed'}</Button>}
		>
			{state.loading ? (
				<CircularProgress />
			) : (
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography variant="h6">Local</Typography>
						{(state.devices || []).length === 0 ? (
							<Typography variant="body2">No local NDI devices found.</Typography>
						) : (
							(state.devices || []).map((d, i) => {
								const raw = (typeof d === 'string') ? d : (d.name || d.raw || d.line || '');
								const name = extractNameFromLine(raw) || raw;
								return (
									<Grid container key={i} alignItems="center" spacing={1}>
										<Grid item xs={8}>
											<Typography>{name}</Typography>
										</Grid>
										<Grid item xs={4}>
											<Button size="small" variant="outlined" onClick={() => { setState((s) => ({ ...s, selected: name })); doPreview(name); }}>Select</Button>
										</Grid>
									</Grid>
								);
							})
						)}
					</Grid>

					<Grid item xs={12}>
						<Typography variant="h6">mDNS</Typography>
						{(state.mdns || []).length === 0 ? (
							<Typography variant="body2">No mDNS NDI devices found.</Typography>
						) : (
							(state.mdns || []).map((d, i) => {
								const raw = (typeof d === 'string') ? d : (d.name || d.raw || d.line || '');
								const name = extractNameFromLine(raw) || raw;
								return (
									<Grid container key={'mdns-' + i} alignItems="center" spacing={1}>
										<Grid item xs={8}><Typography>{name}</Typography></Grid>
										<Grid item xs={4}><Button size="small" variant="outlined" onClick={() => { setState((s) => ({ ...s, selected: name })); doPreview(name); }}>Select</Button></Grid>
									</Grid>
								);
							})
						)}
					</Grid>

					<Grid item xs={12}>
						<Typography variant="h6">Avahi</Typography>
						{(state.avahi || []).length === 0 ? (
							<Typography variant="body2">No Avahi NDI devices found.</Typography>
						) : (
							(state.avahi || []).map((d, i) => {
								const raw = (typeof d === 'string') ? d : (d.name || d.raw || d.line || '');
								const name = extractNameFromLine(raw) || raw;
								return (
									<Grid container key={'avahi-' + i} alignItems="center" spacing={1}>
										<Grid item xs={8}><Typography>{name}</Typography></Grid>
										<Grid item xs={4}><Button size="small" variant="outlined" onClick={() => { setState((s) => ({ ...s, selected: name })); doPreview(name); }}>Select</Button></Grid>
									</Grid>
								);
							})
						)}
					</Grid>

					{state.previewLoading ? (
						<Grid item xs={12}><CircularProgress /></Grid>
					) : state.preview ? (
						<Grid item xs={12}><img src={state.preview} alt="NDI preview" style={{ maxWidth: '100%' }} /></Grid>
					) : null}
				</Grid>
			)}
		</Dialog>
	);
}
